import {
  Banner,
  Card,
  DatePicker,
  Flex,
  HelpIndicator,
  InfoBox,
  RadioButton,
  RadioGroup,
  Switch,
  Text,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { BatchPeriod } from "~/graphql/supergraphEnums";
import { SPACING } from "~/styles/variables";
import { toMMDDYYYYFormat } from "~/utils/datetime";

export type SettingCardProps = {
  glStartDate: Date | null;
  setGlStartDate: (val: Date | null) => void;
  setUseCustomStartDate: (val: boolean) => void;
  useCustomStartDate: boolean;
  entityName: string;
  arenaName: string;
  batchPeriod: "daily" | "monthly" | "quarterly" | null;
  setBatchPeriod: ((val: "daily" | "monthly" | "quarterly") => void) | null;
  transactionCategoryTitle: string;
  showReportingInterval: boolean;
  setChecked: (val: boolean) => void;
  checked: boolean;
  currentGlStartDate: Date | null;
  currentUseCustomGlStartDate: boolean;
  currentBatchPeriod: "daily" | "monthly" | "quarterly" | null;
  customHelpText?: string | null;
};

const FIRST_DATE_OF_MONTH = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
const LAST_DAY_PREVIOUS_MONTH = new Date(new Date().getFullYear(), new Date().getMonth(), 0);

export const SettingCard = ({
  glStartDate,
  setGlStartDate,
  setUseCustomStartDate,
  useCustomStartDate,
  entityName,
  arenaName,
  batchPeriod,
  setBatchPeriod,
  transactionCategoryTitle,
  showReportingInterval,
  setChecked,
  checked,
  currentGlStartDate,
  currentUseCustomGlStartDate,
  currentBatchPeriod,
  customHelpText,
}: SettingCardProps) => {
  const handleChange = () => {
    if (checked == true) {
      setGlStartDate(currentGlStartDate);
      setUseCustomStartDate(currentUseCustomGlStartDate);
      if (setBatchPeriod != null && currentBatchPeriod != null) {
        setBatchPeriod(currentBatchPeriod);
      }
    }
    setChecked(!checked);
  };
  const sinceInceptionHelp = `There are no existing ${transactionCategoryTitle.toLowerCase().replace(/s$/, "")} transactions for ${entityName} in ${arenaName}'s workspace`;
  const customDateHelp = `There are existing ${transactionCategoryTitle.toLowerCase().replace(/s$/, "")} transactions for  ${entityName} in ${arenaName}'s workspace`;
  const quarterlyHelp =
    "e.g. Consolidate all Q2 2024 net income GL transactions into a single JSQ batch dated Jun 30, 2024";
  const monthlyHelp =
    "e.g. Consolidate all April net income GL transactions into a single batch dated Apr 30, 2024";
  const dailyHelp =
    "e.g. Consolidate all net income GL transactions with a GL effective date May 10, 2024 into a single batch";

  const defaultHelpText = `Choose the GL integration start date based on the beginning date of the next reporting
  period (e.g. Choose ${toMMDDYYYYFormat(FIRST_DATE_OF_MONTH)} if transactions already exist
  in ${arenaName}'s workspace through ${toMMDDYYYYFormat(LAST_DAY_PREVIOUS_MONTH)})`;

  const helpText = <Text>{customHelpText ? customHelpText : defaultHelpText}</Text>;
  const activeSwitch = (
    <Switch
      label="Enabled"
      uncheckedLabel="Disabled"
      onChange={handleChange}
      size="medium"
      labelPosition="left"
      checked={checked}
    />
  );
  const disableInfoBoxTitle = `${transactionCategoryTitle.replace(/s$/, "")} GL transactions will no longer sync onto Juniper Square`;
  return (
    // style here make the takeover full screen
    <Flex>
      <Card title={transactionCategoryTitle} actionComponent={activeSwitch} isFullWidth={true}>
        {!checked && <InfoBox variant="info" title={disableInfoBoxTitle} />}
        {checked && (
          <div>
            <Text variant="h4">GL integration start date</Text>
            <Text variant="secondary-md">
              GL integration start date is the earliest GL date from which GL transactions will
              be used to create JSQ {transactionCategoryTitle.toLowerCase().replace(/s$/, "")}{" "}
              transactions.
            </Text>
            <RadioGroup
              name={"startDate" + transactionCategoryTitle}
              value={useCustomStartDate ? "custom" : "none"}
              onChange={(e) => {
                setGlStartDate(null);
                setUseCustomStartDate(e.target.value === "custom" ? true : false);
              }}
            >
              <Flex alignItems="center" css={{ paddingTop: SPACING.SMALL }}>
                <RadioButton value="none">Since inception</RadioButton>
                <HelpIndicator id={entityName} content={sinceInceptionHelp}>
                  <Icon
                    css={{ marginLeft: SPACING.SMALL }}
                    type="question-circle"
                    variant="outline"
                  />
                </HelpIndicator>
              </Flex>
              <Flex alignItems="center" css={{ paddingBottom: SPACING.X_SMALL }}>
                <RadioButton value="custom">Custom start date</RadioButton>
                <HelpIndicator id={entityName} content={customDateHelp}>
                  <Icon
                    css={{ marginLeft: SPACING.SMALL }}
                    type="question-circle"
                    variant="outline"
                  />
                </HelpIndicator>
              </Flex>
            </RadioGroup>
          </div>
        )}
        {checked && useCustomStartDate && (
          <div>
            <Text css={{ paddingTop: SPACING.MEDIUM }}>Start date</Text>
            <DatePicker
              selected={glStartDate}
              onChange={(date: Date | null) => setGlStartDate(date)}
              hasIcon
              isFull
              css={{ paddingBottom: SPACING.MEDIUM }}
            />
            <Banner
              variant="primary"
              title={`${transactionCategoryTitle.replace(/s$/, "")} transactions already exist for ${entityName} in ${arenaName}'s
      workspace`}
              description={helpText}
              css={{ marginBottom: SPACING.X_SMALL }}
            />
          </div>
        )}
        {checked && showReportingInterval && batchPeriod && setBatchPeriod && (
          <div>
            <Text
              variant="h4"
              css={{ paddingTop: SPACING.LARGE, paddingBottom: SPACING.SMALL }}
            >
              JSQ net income reporting interval
            </Text>
            <RadioGroup
              name="batchPeriod"
              value={batchPeriod}
              onChange={(e) => setBatchPeriod(e.target.value as BatchPeriod)}
            >
              <Flex alignItems="center">
                <RadioButton value={BatchPeriod.Quarterly}>Quarterly</RadioButton>
                <HelpIndicator id="quarterly" content={quarterlyHelp}>
                  <Icon
                    css={{ marginLeft: SPACING.SMALL }}
                    type="question-circle"
                    variant="outline"
                  />
                </HelpIndicator>
              </Flex>
              <Flex alignItems="center">
                <RadioButton value={BatchPeriod.Monthly}>Monthly</RadioButton>
                <HelpIndicator id="monthly" content={monthlyHelp}>
                  <Icon
                    css={{ marginLeft: SPACING.SMALL }}
                    type="question-circle"
                    variant="outline"
                  />
                </HelpIndicator>
              </Flex>
              <Flex alignItems="center">
                <RadioButton value={BatchPeriod.Daily}>Daily</RadioButton>
                <HelpIndicator id="daily" content={dailyHelp}>
                  <Icon
                    css={{ marginLeft: SPACING.SMALL }}
                    type="question-circle"
                    variant="outline"
                  />
                </HelpIndicator>
              </Flex>
            </RadioGroup>
          </div>
        )}
      </Card>
    </Flex>
  );
};
